import { BotMessageSquare } from "lucide-react";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { retryStartup, useStartupStatus } from "@noa/client-store";
import { useWarmupProgress } from "@noa/ui";
import { Avatar, Button, Card, Progress } from "~/components/ui";

const TYPING_DOT_DELAYS = ["0ms", "150ms", "300ms"];

/**
 * Stands in for the greeting while a scaled-to-zero API starts, which takes over a minute — an
 * assistant bubble saying so, with an estimated progress bar, or a retry once no session came.
 */
export const ChatWarmupNotice: FunctionComponent = () => {
  const { t } = useTranslation();
  const status = useStartupStatus();
  const progress = useWarmupProgress();

  if (status === "idle") return null;

  return (
    <Card className="chat-message assistant" size="small">
      <div className="flex items-start gap-3">
        <Avatar className="chat-avatar" icon={<BotMessageSquare className="chat-avatar-icon" />} />

        <div className="flex-1 min-w-0 pt-1 self-center flex flex-col gap-2" role="status">
          {status === "warming" ? (
            <>
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex gap-1" aria-hidden>
                  {TYPING_DOT_DELAYS.map((delay) => (
                    <span
                      key={delay}
                      className="size-1.5 rounded-full bg-noa-muted animate-bounce"
                      style={{ animationDelay: delay }}
                    />
                  ))}
                </span>
                {t("chat:warmup.starting")}
              </span>
              <Progress percent={progress} size="small" showInfo={false} aria-label={t("chat:warmup.starting")} />
            </>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-3">
              {t("chat:warmup.failed")}
              <Button size="small" onClick={retryStartup}>
                {t("common:retry")}
              </Button>
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};
